// Description: Page to add one list with one integration
// Updated: 09/10/2025
// Author: Simon Franklin

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { consultIntegrations } from "../../../../../services/api/Integrations/listIntegrations";
import { consultRegisteredIntegrations } from "../../../../../services/api/Integrations/listRegisteredIntegrations";
import use123zapStore from "../../../../../context/123zapStore";
import HeaderPage from "../../../../../shared/components/HeaderPage/HeaderPage";
import { getTokenData, notify, removeKey } from "../../../../../shared/utils/utils";
import { isFeatureAllowed } from "../../../../../shared/utils/featureAccess";
import NotFound from "../../../../../shared/components/common/ui/NotFound";
import { useWizard } from "../../../../../shared/components/common/ui/Wizard/useWizard";
import { WizardStepper } from "../../../../../shared/components/common/ui/Wizard/WizardStepper";
import { WizardFooter } from "../../../../../shared/components/common/ui/Wizard/WizardFooter";
import ErpFormField from "./components/ErpFormField";
import {
  getErpModuleByKey,
  getErpModuleOptions,
} from "./config/erpModulesConfig";
import { resolveIntegrationKey } from "../../../../../shared/constants/integrations";

const DATE_KEYS = [
  "data_vencimento_inicio",
  "data_vencimento_fim",
  "data_inicio",
  "data_fim",
];

const STEPS = [
  { id: "basic", label: "Informações básicas" },
  { id: "filters", label: "Filtragem" },
  { id: "additional", label: "Adicionais" },
];

function formatDate(dateInput) {
  const date = new Date(dateInput);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatFormStateDates(formState) {
  const formattedState = { ...formState };

  DATE_KEYS.forEach((key) => {
    const value = formattedState[key];
    if (!value) return;

    if (typeof value === "object" && value.$d instanceof Date) {
      formattedState[key] = formatDate(value.$d);
      return;
    }

    if (value instanceof Date) {
      formattedState[key] = formatDate(value);
      return;
    }

    if (typeof value === "string" && value.includes("T")) {
      formattedState[key] = formatDate(value);
    }
  });

  return formattedState;
}

function normalizeChipValues(rawValue) {
  const toPrimitive = (item) => {
    const asString = String(item).trim();
    if (!asString) return null;
    return /^\d+$/.test(asString) ? Number(asString) : asString;
  };

  const flattenValues = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") return value.split(",");
    if (value === null || value === undefined) return [];
    return [value];
  };

  const values = flattenValues(rawValue)
    .map(toPrimitive)
    .filter((item) => item !== null);

  if (values.length === 0) return undefined;
  if (values.length === 1) return values[0];
  return values;
}

function getCredentialDisplayName(credential) {
  const label = String(credential?.label || "").trim();
  const integrationTitle = String(credential?.integration_title || "").trim();

  if (credential?.is_default && (!label || label === integrationTitle)) {
    return `${integrationTitle || "Conexão"} - Principal`;
  }

  return label || integrationTitle || "Conexão";
}

function buildInitialFormState(fields = []) {
  return fields.reduce(
    (acc, field) => {
      if (!field?.name) return acc;
      acc[field.name] = field.initialValue ?? "";
      return acc;
    },
    {
      title: "",
      desc: "",
    },
  );
}

function BasicInfoStep({
  formState,
  setFormState,
  moduleOptions,
  selectedModule,
  setSelectedModule,
  credentialOptions,
  selectedCredentialId,
  setSelectedCredentialId,
  credentialsLoading,
  showErrors,
  showModuleError,
}) {
  const titleMissing = showErrors && !String(formState.title || "").trim();
  const descMissing = showErrors && !String(formState.desc || "").trim();

  return (
    <Box sx={{ width: "100%", maxWidth: 900 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Nome da listagem"
            placeholder="Ex: Faturas em aberto - Março"
            value={formState.title || ""}
            onChange={(event) =>
              setFormState((prev) => ({ ...prev, title: event.target.value }))
            }
            error={titleMissing}
            helperText={titleMissing ? "Nome da listagem é obrigatório" : null}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Descrição"
            placeholder="Ex: Listagem para cobrança de clientes com faturas pendentes"
            multiline
            minRows={3}
            value={formState.desc || ""}
            onChange={(event) =>
              setFormState((prev) => ({ ...prev, desc: event.target.value }))
            }
            error={descMissing}
            helperText={descMissing ? "Descrição é obrigatória" : null}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <FormControl fullWidth error={showModuleError}>
            <InputLabel id="erp-module-select-label">Módulo</InputLabel>
            <Select
              labelId="erp-module-select-label"
              value={selectedModule}
              label="Módulo"
              onChange={(event) => setSelectedModule(event.target.value)}
            >
              {moduleOptions.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {showModuleError ? (
              <FormHelperText>Módulo é obrigatório</FormHelperText>
            ) : null}
          </FormControl>
        </Grid>

        {credentialsLoading || credentialOptions.length > 0 ? (
          <Grid item xs={12} md={8}>
            <FormControl fullWidth disabled={credentialsLoading}>
              <InputLabel id="erp-credential-select-label">Conexão</InputLabel>
              <Select
                labelId="erp-credential-select-label"
                value={selectedCredentialId}
                label="Conexão"
                onChange={(event) => setSelectedCredentialId(event.target.value)}
              >
                {credentialOptions.map((credential) => (
                  <MenuItem key={credential.id} value={String(credential.id)}>
                    {getCredentialDisplayName(credential)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {credentialsLoading
                  ? "Carregando conexões disponíveis"
                  : "A listagem ficará vinculada à conexão selecionada"}
              </FormHelperText>
            </FormControl>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}

function FilterStep({ renderedFields, formState, setFormState, fieldErrors = {} }) {
  if (renderedFields.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: 900,
        }}
      >
        <CircularProgress sx={{ color: "rgb(52, 152, 219)" }} />
      </Box>
    );
  }

  const moduleDateFieldNames = renderedFields
    .filter((input) => input.type === "date")
    .map((input) => input.name);

  const dateFields = renderedFields.filter((input) => input.type === "date");
  const firstDateFieldName = dateFields[0]?.name;

  return (
    <Box sx={{ width: "100%", maxWidth: 900 }}>
      <Grid container spacing={3}>
        {renderedFields.map((input) => {
          if (input.type === "date" && input.name !== firstDateFieldName) {
            return null;
          }

          if (input.type === "date" && input.name === firstDateFieldName) {
            return (
              <Grid item xs={12} md={8} key="date-fields-block">
                <Grid container spacing={3}>
                  {dateFields.map((dateInput) => (
                    <Grid item xs={12} sm={6} key={dateInput.name}>
                      <ErpFormField
                        input={dateInput}
                        formState={formState}
                        setFormState={setFormState}
                        moduleDateFieldNames={moduleDateFieldNames}
                        error={Boolean(fieldErrors[dateInput.name])}
                        helperText={fieldErrors[dateInput.name] || ""}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            );
          }

          return (
            <Grid item xs={12} md={8} key={input.name}>
              <ErpFormField
                input={input}
                formState={formState}
                setFormState={setFormState}
                moduleDateFieldNames={moduleDateFieldNames}
                error={Boolean(fieldErrors[input.name])}
                helperText={fieldErrors[input.name] || ""}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function AdditionalStep({ paymentSystem, setPaymentSystem }) {
  return (
    <Box sx={{ width: "100%", maxWidth: 900 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <FormControl fullWidth>
            <InputLabel id="payment-system-select-label">
              Consulta adicional (opcional)
            </InputLabel>
            <Select
              labelId="payment-system-select-label"
              value={paymentSystem}
              label="Consulta adicional (opcional)"
              onChange={(event) => setPaymentSystem(event.target.value)}
            >
              <MenuItem value="">Nenhum</MenuItem>
              <MenuItem value="bemobi_7az">Bemobi (7AZ)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

const AddSheetsIntegrationInvoice = () => {
  const { integration } = useParams();
  const navigate = useNavigate();

  const creatingCampaignWithHelpMode =
    JSON.parse(sessionStorage.getItem("creating_campaign")) || null;

  const featureAccess = use123zapStore((state) => state.featureAccess);
  const integrations = use123zapStore((state) => state.integrations);
  const setIntegrations = use123zapStore((state) => state.setIntegrations);
  const setAlreadyFetchSheets = use123zapStore(
    (state) => state.setAlreadyFetchSheets,
  );
  const alreadyFetchIntegrations = use123zapStore(
    (state) => state.alreadyFetchIntegrations,
  );
  const setAlreadyFetchIntegrations = use123zapStore(
    (state) => state.setAlreadyFetchIntegrations,
  );

  const [formState, setFormState] = useState({});
  const [disableButton, setDisableButton] = useState(false);
  const [, setOpenModalCampaign] = useState(false);
  const [selectedModule, setSelectedModule] = useState("");
  const [showModuleError, setShowModuleError] = useState(false);
  const [attemptedBasicNext, setAttemptedBasicNext] = useState(false);
  const [filterErrors, setFilterErrors] = useState({});
  const [siengePaymentTermsOptions, setSiengePaymentTermsOptions] = useState([]);
  const [siengePaymentTermsLoading, setSiengePaymentTermsLoading] = useState(false);
  const [siengePaymentTermsError, setSiengePaymentTermsError] = useState("");
  const [ixcCancellationOptions, setIxcCancellationOptions] = useState([]);
  const [ixcCancellationLoading, setIxcCancellationLoading] = useState(false);
  const [ixcCancellationError, setIxcCancellationError] = useState("");
  const [credentialOptions, setCredentialOptions] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [selectedCredentialId, setSelectedCredentialId] = useState("");
  const [paymentSystem, setPaymentSystem] = useState("");

  const wizard = useWizard({
    steps: STEPS,
    initialStepId: "basic",
  });

  const isPlanAllowed = isFeatureAllowed(featureAccess, "ERP", true);

  const selectedIntegrationKey = useMemo(
    () => resolveIntegrationKey(integration) || integration,
    [integration],
  );

  const selectedIntegration = useMemo(
    () =>
      integrations.find((item) =>
        resolveIntegrationKey(item.title || item.key || item.id) === selectedIntegrationKey,
      ) || null,
    [integrations, selectedIntegrationKey],
  );

  const moduleOptions = useMemo(
    () => getErpModuleOptions(selectedIntegration?.title),
    [selectedIntegration],
  );

  const selectedModuleConfig = useMemo(
    () => getErpModuleByKey(selectedIntegration?.title, selectedModule),
    [selectedIntegration, selectedModule],
  );

  useEffect(() => {
    if (alreadyFetchIntegrations) return;

    consultIntegrations()
      .then((response) => {
        setIntegrations(response.data);
      })
      .finally(() => {
        setAlreadyFetchIntegrations(true);
      });
  }, [alreadyFetchIntegrations, setAlreadyFetchIntegrations, setIntegrations]);

  useEffect(() => {
    setSelectedModule("");
    setShowModuleError(false);
    setAttemptedBasicNext(false);
    setFilterErrors({});
    setPaymentSystem("");
    setCredentialOptions([]);
    setSelectedCredentialId("");
    setFormState((prev) => ({ title: prev.title || "", desc: prev.desc || "" }));
  }, [selectedIntegration?.title]);

  useEffect(() => {
    if (!selectedIntegration?.title) return;

    let ignore = false;

    const fetchCredentials = async () => {
      setCredentialsLoading(true);
      try {
        const response = await consultRegisteredIntegrations();
        const payload = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const selectedTitleKey = resolveIntegrationKey(selectedIntegration.title);
        const filteredCredentials = payload
          .filter(
            (item) =>
              resolveIntegrationKey(item?.integration_title) === selectedTitleKey,
          )
          .sort((a, b) => {
            if (Boolean(a?.is_default) !== Boolean(b?.is_default)) {
              return a?.is_default ? -1 : 1;
            }
            return String(a?.label || "").localeCompare(String(b?.label || ""), "pt-BR", {
              sensitivity: "base",
            });
          });

        if (ignore) return;

        setCredentialOptions(filteredCredentials);
        const defaultCredential =
          filteredCredentials.find((item) => item?.is_default) ||
          filteredCredentials[0];
        setSelectedCredentialId(defaultCredential?.id ? String(defaultCredential.id) : "");
      } catch (error) {
        if (!ignore) {
          setCredentialOptions([]);
          setSelectedCredentialId("");
        }
      } finally {
        if (!ignore) {
          setCredentialsLoading(false);
        }
      }
    };

    fetchCredentials();

    return () => {
      ignore = true;
    };
  }, [selectedIntegration?.title]);

  useEffect(() => {
    if (!selectedModuleConfig) return;

    setFilterErrors({});
    setPaymentSystem("");
    setFormState((prev) => ({
      ...buildInitialFormState(selectedModuleConfig.inputs || []),
      title: prev.title || "",
      desc: prev.desc || "",
    }));
  }, [selectedModuleConfig]);

  useEffect(() => {
    if (selectedIntegration?.title !== "Sienge") {
      setSiengePaymentTermsOptions([]);
      setSiengePaymentTermsLoading(false);
      setSiengePaymentTermsError("");
      return;
    }

    let ignore = false;

    const fetchSiengePaymentTerms = async () => {
      setSiengePaymentTermsLoading(true);
      setSiengePaymentTermsError("");

      try {
        const { token, client_id } = await getTokenData();

        if (!token) throw new Error("Token de autenticação não encontrado");
        if (!client_id) throw new Error("ID do cliente não encontrado");

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_AWS_URL}/erp/sienge/payment-condition-types?limit=200`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `${token}`,
              Client_ID: `${client_id}`,
            },
          },
        );

        const jsonResponse = await response.json();

        if (!response.ok || jsonResponse?.status !== "success") {
          throw new Error(jsonResponse?.message || "Falha ao consultar tipos de parcela do Sienge");
        }

        const rawData = jsonResponse?.data;
        let parsedData = rawData;

        if (typeof parsedData === "string") {
          try {
            parsedData = JSON.parse(parsedData);
          } catch (_) {
            parsedData = [];
          }
        }

        const results = Array.isArray(parsedData)
          ? parsedData
          : Array.isArray(parsedData?.results)
            ? parsedData.results
            : Array.isArray(parsedData?.data)
              ? parsedData.data
              : Array.isArray(jsonResponse?.results)
                ? jsonResponse.results
                : [];

        const mappedOptions = results
          .filter((item) => item && item.id !== undefined && item.id !== null)
          .map((item) => ({
            value: String(item.id),
            label: item.description
              ? `${item.description} (${item.id})`
              : String(item.id),
          }));

        if (!ignore) {
          setSiengePaymentTermsOptions(mappedOptions);
        }
      } catch (error) {
        if (!ignore) {
          setSiengePaymentTermsOptions([]);
          setSiengePaymentTermsError(
            "Não foi possível carregar os tipos de parcela. Você pode continuar sem esse filtro.",
          );
        }
      } finally {
        if (!ignore) {
          setSiengePaymentTermsLoading(false);
        }
      }
    };

    fetchSiengePaymentTerms();

    return () => {
      ignore = true;
    };
  }, [selectedIntegration?.title]);

  useEffect(() => {
    if (selectedIntegration?.title !== "IXC Soft") {
      setIxcCancellationOptions([]);
      setIxcCancellationLoading(false);
      setIxcCancellationError("");
      return;
    }

    let ignore = false;

    const fetchIxcCancellationReasons = async () => {
      setIxcCancellationLoading(true);
      setIxcCancellationError("");

      try {
        const { token, client_id } = await getTokenData();

        if (!token) throw new Error("Token de autenticação não encontrado");
        if (!client_id) throw new Error("ID do cliente não encontrado");

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_AWS_URL}/erp/ixc/cancellation-reasons`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `${token}`,
              Client_ID: `${client_id}`,
            },
          },
        );

        const jsonResponse = await response.json();
        const effectiveResponse =
          jsonResponse && typeof jsonResponse.body === "string"
            ? (() => {
                try {
                  return JSON.parse(jsonResponse.body);
                } catch (_) {
                  return jsonResponse;
                }
              })()
            : jsonResponse;

        if (!response.ok || effectiveResponse?.status !== "success") {
          throw new Error(
            effectiveResponse?.message || "Falha ao consultar motivos de cancelamento do IXC",
          );
        }

        const rawData = effectiveResponse?.data;
        let parsedData = rawData;

        if (typeof parsedData === "string") {
          try {
            parsedData = JSON.parse(parsedData);
          } catch (_) {
            parsedData = [];
          }
        }

        const results = Array.isArray(parsedData)
          ? parsedData
          : Array.isArray(parsedData?.results)
            ? parsedData.results
            : Array.isArray(parsedData?.data)
              ? parsedData.data
              : Array.isArray(effectiveResponse?.results)
                ? effectiveResponse.results
                : [];

        const mappedOptions = results
          .filter((item) => item && item.id !== undefined && item.id !== null)
          .map((item) => ({
            value: String(item.id),
            label:
              (item.motivo || item.description || item.descricao || item.nome)
                ? `${item.motivo || item.description || item.descricao || item.nome} (${item.id})`
                : String(item.id),
          }));

        console.log("[IXC cancellation] raw response:", jsonResponse);
        console.log("[IXC cancellation] effective response:", effectiveResponse);
        console.log("[IXC cancellation] parsed results:", results);
        console.log("[IXC cancellation] mapped options:", mappedOptions);

        if (!ignore) {
          setIxcCancellationOptions(mappedOptions);
        }
      } catch (error) {
        if (!ignore) {
          setIxcCancellationOptions([]);
          setIxcCancellationError(
            "Não foi possível carregar os motivos de cancelamento do IXC. Você pode continuar sem esse filtro.",
          );
        }
      } finally {
        if (!ignore) {
          setIxcCancellationLoading(false);
        }
      }
    };

    fetchIxcCancellationReasons();

    return () => {
      ignore = true;
    };
  }, [selectedIntegration?.title]);

  const isEmptyValue = (value) => {
    if (Array.isArray(value)) return value.length === 0;
    return String(value ?? "").trim() === "";
  };

  const isInputRequired = (input, currentFormState) => {
    if (input?.required) return true;
    if (!input?.requiredWhen) return false;

    const { field, equals } = input.requiredWhen;
    return currentFormState[field] === equals;
  };

  const validateFilterStep = (currentFormState) => {
    const nextErrors = {};

    integrationInputs
      .filter((input) => input.type !== "hidden")
      .forEach((input) => {
        if (!isInputRequired(input, currentFormState)) return;

        if (isEmptyValue(currentFormState[input.name])) {
          nextErrors[input.name] = `${input.label || "Campo"} é obrigatório`;
        }
      });

    return nextErrors;
  };

  const getEmptyOptionalInputs = (currentFormState) =>
    integrationInputs.filter((input) => {
      if (input.type === "hidden") return false;
      if (
        input.type === "date" &&
        currentFormState.periodo_dinamico_mensal === "sim"
      ) {
        return false;
      }
      if (isInputRequired(input, currentFormState)) return false;
      return isEmptyValue(currentFormState[input.name]);
    });

  useEffect(() => {
    if (Object.keys(filterErrors).length === 0) return;

    const nextErrors = validateFilterStep(formState);
    setFilterErrors((prev) => {
      const changed =
        Object.keys(prev).length !== Object.keys(nextErrors).length ||
        Object.keys(prev).some((key) => prev[key] !== nextErrors[key]);
      return changed ? nextErrors : prev;
    });
  }, [formState]); // eslint-disable-line react-hooks/exhaustive-deps

  const integrationInputs = useMemo(() => {
    const inputs = selectedModuleConfig?.inputs || [];

    return inputs.map((input) => {
      if (selectedIntegration?.title === "Sienge" && input.name === "paymentTermsId") {
        return {
          ...input,
          options: siengePaymentTermsOptions,
          loadingOptions: siengePaymentTermsLoading,
          helpText: siengePaymentTermsError || input.helpText,
        };
      }

      if (selectedIntegration?.title === "IXC Soft" && input.name === "id_motivo_cancelamento") {
        return {
          ...input,
          options: ixcCancellationOptions,
          loadingOptions: ixcCancellationLoading,
          helpText: ixcCancellationError || input.helpText,
        };
      }

      return input;
    });
  }, [
    selectedModuleConfig,
    selectedIntegration?.title,
    siengePaymentTermsOptions,
    siengePaymentTermsLoading,
    siengePaymentTermsError,
    ixcCancellationOptions,
    ixcCancellationLoading,
    ixcCancellationError,
  ]);

  const renderedFields = useMemo(
    () => {
      const visibleInputs = integrationInputs.filter(
        (input) => input.type !== "hidden",
      );

      const filteredInputs = visibleInputs.filter(
        (input) => input.name !== "title" && input.name !== "desc",
      );

      const getPriority = (input) => {
        if (input.name === "status" || input.name === "apenas_quitado" || input.name === "status_contrato" || input.name === "contrato_status") return 0;
        if (input.name === "periodo_dinamico_mensal") return 1;
        if (input.type === "date") return 2;
        return 3;
      };

      return filteredInputs
        .map((input, index) => ({ input, index }))
        .sort((a, b) => {
          const priorityDiff = getPriority(a.input) - getPriority(b.input);
          if (priorityDiff !== 0) return priorityDiff;
          return a.index - b.index;
        })
        .map((item) => item.input);
    },
    [integrationInputs],
  );

  const handleSubmit = async () => {
    setDisableButton(true);

    const allowedKeys = new Set([
      "title",
      "desc",
      ...integrationInputs.map((input) => input.name),
    ]);
    const scopedFormState = Object.fromEntries(
      Object.entries(formState).filter(([key]) => allowedKeys.has(key)),
    );
    const cleanedFormState = removeKey(scopedFormState, "status_invoice");
    const normalizedFormState = { ...cleanedFormState };

    integrationInputs
      .filter((input) => input.type === "chips")
      .forEach((input) => {
        const normalizedChipValue = normalizeChipValues(
          normalizedFormState[input.name],
        );

        if (normalizedChipValue === undefined) {
          delete normalizedFormState[input.name];
          return;
        }

        normalizedFormState[input.name] = normalizedChipValue;
      });

    if (Object.prototype.hasOwnProperty.call(normalizedFormState, "periodo_dinamico_mensal")) {
      const isDynamicMonthlyPeriodEnabled =
        normalizedFormState.periodo_dinamico_mensal === "sim" ||
        normalizedFormState.periodo_dinamico_mensal === true;

      normalizedFormState.periodo_dinamico_mensal = isDynamicMonthlyPeriodEnabled;

      if (isDynamicMonthlyPeriodEnabled) {
        integrationInputs
          .filter((input) => input.type === "date")
          .forEach((input) => {
            delete normalizedFormState[input.name];
          });
      }
    }

    const formattedFormState = formatFormStateDates(normalizedFormState);
    formattedFormState.module = selectedModule;

    if (selectedCredentialId) {
      formattedFormState.credential_id = selectedCredentialId;
    }

    if (paymentSystem === "bemobi_7az") {
      formattedFormState.use_7az = true;
    }

    const finalRoute = resolveIntegrationKey(selectedIntegration?.title);
    const finalEndpointPath = selectedModuleConfig?.endpointPath;

    try {
      const { token, client_id } = await getTokenData();

      if (!token) throw new Error("Token de autenticação não encontrado");
      if (!client_id) throw new Error("ID do cliente não encontrado");
      if (!finalEndpointPath) throw new Error("Módulo inválido para a integração selecionada");

      await fetch(
        `${process.env.REACT_APP_BACKEND_AWS_URL}/erp/${finalRoute}/${finalEndpointPath}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
            Client_ID: `${client_id}`,
          },
          body: JSON.stringify(formattedFormState),
        },
      )
        .then((response) => response.json())
        .then((response) => {
          if (response.status === "success") {
            if (creatingCampaignWithHelpMode) {
              setOpenModalCampaign(true);
            } else {
              navigate("/listagens");
            }

            notify("Success", "Listagem criada com sucesso", true);
            setAlreadyFetchSheets(false);
          }
        });
    } catch (error) {
      console.error(error);
      throw new Error("Erro ao buscar dados da API");
    } finally {
      setDisableButton(false);
    }
  };

  const handleNext = async () => {
    if (disableButton) return;

    if (wizard.currentStep.id === "basic") {
      const hasTitle = String(formState.title || "").trim().length > 0;
      const hasDesc = String(formState.desc || "").trim().length > 0;
      const hasModule = !!selectedModule;

      setAttemptedBasicNext(true);
      setShowModuleError(!hasModule);

      if (!hasTitle || !hasDesc || !hasModule) {
        return;
      }

      setAttemptedBasicNext(false);
      setShowModuleError(false);
      setFilterErrors({});
      wizard.next();
      return;
    }

    if (wizard.currentStep.id === "filters") {
      const nextErrors = validateFilterStep(formState);
      setFilterErrors(nextErrors);

      if (Object.keys(nextErrors).length > 0) {
        return;
      }

      const optionalBlankInputs = getEmptyOptionalInputs(formState);
      if (optionalBlankInputs.length > 0) {
        const previewLabels = optionalBlankInputs
          .slice(0, 3)
          .map((input) => input.label || input.name);
        const hasMore = optionalBlankInputs.length > 3;

        const confirmed = window.confirm(
          `Existem campos opcionais em branco (${previewLabels.join(", ")}${
            hasMore ? ", ..." : ""
          }). Deseja continuar mesmo assim?`,
        );

        if (!confirmed) return;
      }

      wizard.next();
      return;
    }

    await handleSubmit();
  };

  const stepContent =
    wizard.currentStep.id === "basic" ? (
      <BasicInfoStep
        formState={formState}
        setFormState={setFormState}
        moduleOptions={moduleOptions}
        selectedModule={selectedModule}
        setSelectedModule={setSelectedModule}
        credentialOptions={credentialOptions}
        selectedCredentialId={selectedCredentialId}
        setSelectedCredentialId={setSelectedCredentialId}
        credentialsLoading={credentialsLoading}
        showErrors={attemptedBasicNext}
        showModuleError={showModuleError}
      />
    ) : wizard.currentStep.id === "filters" ? (
      <FilterStep
        renderedFields={renderedFields}
        formState={formState}
        setFormState={setFormState}
        fieldErrors={filterErrors}
      />
    ) : (
      <AdditionalStep
        paymentSystem={paymentSystem}
        setPaymentSystem={setPaymentSystem}
      />
    );

  if (!isPlanAllowed) {
    return <NotFound />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <HeaderPage />

      <Box sx={{ width: "100%", px: 4, py: 3 }}>
        <WizardStepper
          steps={wizard.steps}
          activeStep={wizard.currentStepIndex}
        />

        <Box sx={{ maxWidth: 860 }}>
          {stepContent}
        </Box>

        <WizardFooter
          canGoBack={wizard.canGoBack}
          onBack={wizard.back}
          onNext={handleNext}
          nextLabel={wizard.currentStep.id === "additional" ? "Finalizar" : "Avançar"}
          isSaving={disableButton}
          isNextDisabled={disableButton}
        />
      </Box>
    </Box>
  );
};

export default AddSheetsIntegrationInvoice;
